import { nextLevelDown, nextLevelUp } from './enums.js';
/**
 * Rules-first level suggestion thresholds (section 4). The rules decide whether
 * to suggest; the LLM only writes the one-line reason; the teacher approves.
 *
 * Marked as tunable during the pilot — section 13 leaves the exact numbers open.
 */
export const SUGGESTION_RULES = {
    /** Score at or above this on consecutive sets suggests moving up. */
    promoteAtOrAbove: 85,
    /** Score below this on consecutive sets suggests moving down. */
    demoteBelow: 40,
    /** How many consecutive completed sets the rule looks at. */
    consecutiveSets: 2,
    /** A set shorter than this does not count toward a suggestion. */
    minItemsPerSet: 5,
    /** Do not raise a new suggestion for the same student and topic within this window. */
    cooldownDays: 7,
};
/**
 * Pure function: given a student's recent completed sets at their current level
 * (newest first), decide whether to raise a level suggestion.
 *
 * Returns null when no rule fires. Integration tests cover this directly
 * (CLAUDE.md section 12).
 */
export function evaluateLevelSuggestion(currentLevel, recentSets) {
    const eligible = recentSets
        .filter((s) => s.itemCount >= SUGGESTION_RULES.minItemsPerSet)
        .slice(0, SUGGESTION_RULES.consecutiveSets);
    if (eligible.length < SUGGESTION_RULES.consecutiveSets)
        return null;
    if (eligible.every((s) => s.scorePct >= SUGGESTION_RULES.promoteAtOrAbove)) {
        const toLevel = nextLevelUp(currentLevel);
        if (toLevel)
            return { direction: 'up', toLevel, basis: eligible };
    }
    if (eligible.every((s) => s.scorePct < SUGGESTION_RULES.demoteBelow)) {
        const toLevel = nextLevelDown(currentLevel);
        if (toLevel)
            return { direction: 'down', toLevel, basis: eligible };
    }
    return null;
}
/**
 * Mastery model. A sub-topic score is an exponentially weighted average of
 * attempt outcomes, so recent work moves the number faster than old work.
 *
 * Hint usage is recorded but never penalized (section 3.1), so it is not an
 * input here.
 */
export const MASTERY = {
    /** Weight given to the newest attempt. */
    alpha: 0.3,
    /** Starting score for a sub-topic with no attempts. */
    initial: 50,
    /** At or below this, a sub-topic shows as weak to the student and teacher. */
    weakAtOrBelow: 40,
    /**
     * At or above this, a score is called strong. Lived in `masteryTone`,
     * `masteryColor` and two screens as a bare 70 before it had a fourth caller;
     * a threshold the student sees in a color, a caption and a recommendation has
     * to be one number.
     */
    strongAtOrAbove: 70,
    /** Attempts needed before a score is treated as meaningful. */
    minAttempts: 3,
};
export function updateMastery(previous, correct) {
    const observed = correct ? 100 : 0;
    const next = previous + MASTERY.alpha * (observed - previous);
    return Math.round(Math.min(100, Math.max(0, next)));
}
//# sourceMappingURL=rules.js.map